import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { key: 2 }

import { useI18n } from 'vue-i18n';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useInteractionModeStore } from '@/store/interactionModeStore';
  import { InteractionModeEnum } from '@/types/InteractionModeEnum';
  import { useRoute } from 'vue-router';
  import { getUserRoles } from '@/plugins/keycloak';

  interface TimeToLive {
    total: number;
    minutes: number | string;
    seconds: number | string;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'InteractionMode',
  setup(__props) {

  const { t } = useI18n();
  const route = useRoute();
  const appEditable = ref(false);
  const interactionModeStore = useInteractionModeStore();
  const interactionMode = computed(() => interactionModeStore.mode);
  const editTokenPending = ref(false);
  const editToken = computed(() => {
    const token = interactionModeStore.editToken;
    const tokenPossesedAndActive = isTokenPossesedAndActive(token);

    const shouldEnterEditMode =
      tokenPossesedAndActive && interactionMode.value !== InteractionModeEnum.EDIT;
    if (shouldEnterEditMode) enterEditMode();

    const shouldEnterROMode =
      !tokenPossesedAndActive && interactionMode.value === InteractionModeEnum.EDIT;
    if (shouldEnterROMode) enterReadOnlyMode();

    _editModeCountDownUpd();

    return token;
  });

  const editEvtNames = [
    'keydown',
    'mousedown',
    'pointerdown',
    'touchstart',
    'wheel',
    'paste',
    'input',
  ];
  const editEvtOpts = { passive: true, capture: true };

  let timerID: number | undefined;
  let ttlID: number | undefined;
  const ttl = ref<TimeToLive | undefined>(undefined);

  onMounted(async () => {
    interactionModeStore.setMode(InteractionModeEnum.READONLY); // default mode

    appEditable.value = getUserRoles().includes('ria-editor');
    if (!appEditable.value) return;

    await fetchEditToken();
    await prolongEditMode();
  });

  watch(route, async () => {
    await fetchEditToken();
    await prolongEditMode();
  });

  async function switchInteractionModes() {
    if (editTokenPending.value) return;

    editTokenPending.value = true;
    if (interactionMode.value === InteractionModeEnum.READONLY)
      await interactionModeStore.requestEditMode();
    else if (interactionMode.value === InteractionModeEnum.EDIT)
      await interactionModeStore.leaveEditMode();
    editTokenPending.value = false;
  }

  async function fetchEditToken() {
    return await interactionModeStore.fetchEditToken();
  }

  async function prolongEditMode() {
    if (!isTokenPossesedAndActive(interactionModeStore.editToken)) return;
    await interactionModeStore.prolongEditMode();
  }

  function isTokenPossesedAndActive(token: EditTokenType | undefined) {
    if (!token) return;
    if (token?.currentUserIsHolder) return isTokenActive(token);
  }

  function isTokenActive(token: EditTokenType | undefined) {
    if (!token) return;
    return new Date(token?.exspire) > new Date();
  }

  async function enterEditMode() {
    const contentEl = document.querySelector('.content');
    if (!contentEl) return;

    for (const evtName of editEvtNames) {
      contentEl.addEventListener(evtName, editModeAutoProlong, editEvtOpts);
    }

    interactionModeStore.setMode(InteractionModeEnum.EDIT);
  }

  async function enterReadOnlyMode() {
    const contentEl = document.querySelector('.content');
    if (!contentEl) return;

    for (const evtName of editEvtNames) {
      contentEl.removeEventListener(evtName, editModeAutoProlong, editEvtOpts);
    }

    clearTimeout(timerID);
    timerID = undefined;
    interactionModeStore.setMode(InteractionModeEnum.READONLY);
  }

  async function editModeAutoProlong() {
    if (timerID) return;
    const token = editToken.value;
    let timeoutMS = 60000;
    if (isTokenPossesedAndActive(token)) {
      const now = new Date().getTime();
      const expire = new Date(token?.exspire as string).getTime();
      if (expire - now < 0) return (timerID = undefined); // token expired
      if (expire - now < timeoutMS) timeoutMS = (expire - now) / 2;
    }

    timerID = setTimeout(() => {
      timerID = undefined;
      if (isTokenPossesedAndActive(editToken.value)) prolongEditMode();
    }, timeoutMS);
  }

  function _editModeCountDownUpd() {
    const expireDt = editToken.value?.exspire;

    if (!expireDt) {
      clearInterval(ttlID);
      return (ttlID = undefined);
    }

    if (!ttlID) {
      ttlID = setInterval(() => {
        ttl.value = calculateTimeToEnd(editToken.value?.exspire);
        if (ttl.value && ttl.value.total <= 0) interactionModeStore.leaveEditMode();
      }, 1000);
    }
  }

  function calculateTimeToEnd(dateTimeStr?: string): TimeToLive | undefined {
    if (dateTimeStr) {
      let totalSeconds = Math.ceil((new Date(dateTimeStr).getTime() - new Date().getTime()) / 1000);

      return {
        total: totalSeconds,
        minutes: Math.floor(totalSeconds / 60),
        seconds: (totalSeconds % 60).toString().padStart(2, '0'),
      };
    }

    return undefined;
  }

return (_ctx: any,_cache: any) => {
  const _component_v_switch = _resolveComponent("v-switch")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (appEditable.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["interaction-mode-cont d-flex align-center ga-md-4 px-4 mb-3", { 'bg-red-darken-1': editToken.value && !editToken.value.currentUserIsHolder }])
      }, [
        _createElementVNode("span", null, [
          _createElementVNode("b", null, _toDisplayString(_unref(t)('interactionModes.editMode')), 1)
        ]),
        _createVNode(_component_v_switch, {
          color: "green",
          "hide-details": "",
          disabled: editToken.value && !editToken.value.currentUserIsHolder,
          "model-value": editToken.value?.currentUserIsHolder && interactionMode.value === _unref(InteractionModeEnum).EDIT,
          density: "compact",
          "onUpdate:modelValue": switchInteractionModes
        }, null, 8, ["disabled", "model-value"]),
        (editTokenPending.value)
          ? (_openBlock(), _createBlock(_component_v_progress_circular, {
              key: 0,
              indeterminate: true,
              size: "24",
              color: "green"
            }))
          : _createCommentVNode("", true),
        (editToken.value && editToken.value.currentUserIsHolder && ttl.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_1, " (" + _toDisplayString(_unref(t)('interactionModes.editModeTTL', { minutes: ttl.value.minutes, seconds: ttl.value.seconds })) + ") ", 1))
          : _createCommentVNode("", true),
        (editToken.value && !editToken.value.currentUserIsHolder)
          ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(_unref(t)('interactionModes.contentBeingEdditedBy', {
          name: editToken.value?.firstName,
          surname: editToken.value?.familyName,
        })), 1))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})